import {
  createAudioPlayer,
  setAudioModeAsync,
  type AudioMetadata,
  type AudioSource,
  type AudioStatus,
} from "@-/com.npmjs.expo-audio";
import { getSilenceCarrierAudioSource } from "@-/dev.improve.haniverse.assets.audio.silence";
import {
  startBackgroundMicrophone,
  stopBackgroundMicrophone,
  onBackgroundMicrophoneStop,
} from "@-/dev.improve.haniverse.modules.audio-input";
import {
  onRemoteCommand,
  setRemoteCommandsEnabled,
  type RemoteCommandEvent,
} from "@-/dev.improve.haniverse.modules.remote-commands";

export type VoiceStudyRemoteCommand = "next" | "pause" | "play" | "previous" | "stop";

/** The slice of an expo-audio `AudioPlayer` the carrier needs; injectable for tests. */
export type VoiceStudyCarrierPlayer = {
  addListener: (
    event: "playbackStatusUpdate",
    listener: (status: Pick<AudioStatus, "isLoaded" | "playing" | "timeControlStatus">) => void,
  ) => { remove(): void };
  clearLockScreenControls: () => void;
  loop: boolean;
  pause: () => void;
  play: () => void;
  remove: () => void;
  setActiveForLockScreen: (
    active: boolean,
    metadata?: AudioMetadata,
    options?: { isLiveStream?: boolean },
  ) => void;
  updateLockScreenMetadata: (metadata: AudioMetadata) => void;
};

export type VoiceStudyLockScreenDeps = {
  carrier: AudioSource | null;
  createPlayer: (source: AudioSource) => VoiceStudyCarrierPlayer;
  microphone: {
    onStop: (listener: () => void) => { remove(): void };
    start: () => Promise<void>;
    stop: () => void;
  };
  remote: {
    onRemoteCommand: (listener: (event: RemoteCommandEvent) => void) => { remove(): void };
    setEnabled: (enabled: boolean) => void;
  };
  setAudioMode: () => Promise<void>;
};

export type VoiceStudyLockScreen = {
  activate: (metadata: AudioMetadata) => Promise<void>;
  deactivate: () => void;
  onRemote: (listener: (command: VoiceStudyRemoteCommand) => void) => () => void;
  setPlaying: (playing: boolean) => void;
  updateMetadata: (metadata: AudioMetadata) => void;
};

function defaultDeps(): VoiceStudyLockScreenDeps {
  return {
    carrier: getSilenceCarrierAudioSource(),
    createPlayer: (source) => createAudioPlayer(source, { keepAudioSessionActive: true }),
    microphone: {
      onStop: onBackgroundMicrophoneStop,
      start: startBackgroundMicrophone,
      stop: stopBackgroundMicrophone,
    },
    remote: { onRemoteCommand, setEnabled: setRemoteCommandsEnabled },
    setAudioMode: () =>
      setAudioModeAsync({
        allowsRecording: true,
        interruptionMode: "doNotMix",
        playsInSilentMode: true,
        shouldPlayInBackground: true,
        shouldRouteThroughEarpiece: false,
      }),
  };
}

/**
 * The now-playing card and its transport controls, built on expo-audio.
 *
 * A looping silent "carrier" is the player expo-audio puts on the lock screen:
 * while it plays, iOS keeps the `audio` background mode (and Android its
 * media-playback foreground service) alive with the phone locked, and the card's
 * ▶/⏸ mirrors whether the study session is running. expo-audio owns play/pause
 * natively (the remote command just pauses the carrier), so a status update whose
 * playing flag disagrees with what we last asked for IS the remote press.
 * next/previous come from the RemoteCommands module.
 * Android capture has a separate microphone foreground service; media playback
 * alone keeps playing while Android silences this app's microphone after lock.
 */
export function createVoiceStudyLockScreen(
  deps: VoiceStudyLockScreenDeps = defaultDeps(),
): VoiceStudyLockScreen {
  const listeners = new Set<(command: VoiceStudyRemoteCommand) => void>();
  let player: VoiceStudyCarrierPlayer | null = null;
  let subscriptions: { remove(): void }[] = [];
  let expectedPlaying = false;
  let active = false;
  let activation = 0;

  const emit = (command: VoiceStudyRemoteCommand) => {
    for (const listener of listeners) {
      listener(command);
    }
  };

  return {
    async activate(metadata) {
      if (active) {
        return;
      }
      active = true;
      const token = ++activation;
      subscriptions.push(deps.microphone.onStop(() => emit("stop")));
      await deps.microphone.start();
      if (!active || token !== activation) {
        if (!active) {
          deps.microphone.stop();
        }
        return;
      }
      await deps.setAudioMode();
      if (!active || token !== activation || deps.carrier === null) {
        return;
      }
      const carrier = deps.createPlayer(deps.carrier);
      player = carrier;
      carrier.loop = true;
      expectedPlaying = true;
      carrier.play();
      carrier.setActiveForLockScreen(true, metadata, { isLiveStream: true });
      subscriptions.push(
        carrier.addListener("playbackStatusUpdate", (status) => {
          if (!status.isLoaded) {
            return;
          }
          // Buffering after `play()` also reports `playing: false`; only an explicit pause counts.
          if (expectedPlaying && status.timeControlStatus === "paused") {
            expectedPlaying = false;
            emit("pause");
          } else if (!expectedPlaying && status.playing) {
            expectedPlaying = true;
            emit("play");
          }
        }),
        deps.remote.onRemoteCommand(({ command }) => emit(command)),
      );
      deps.remote.setEnabled(true);
    },
    deactivate() {
      if (!active) {
        return;
      }
      active = false;
      activation += 1;
      deps.microphone.stop();
      deps.remote.setEnabled(false);
      for (const subscription of subscriptions) {
        subscription.remove();
      }
      subscriptions = [];
      if (player !== null) {
        try {
          player.clearLockScreenControls();
          player.pause();
          player.remove();
        } catch {
          // Already released under us (fast refresh).
        }
        player = null;
      }
      expectedPlaying = false;
    },
    onRemote(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setPlaying(playing) {
      expectedPlaying = playing;
      if (player === null) {
        return;
      }
      if (playing) {
        player.play();
      } else {
        player.pause();
      }
    },
    updateMetadata(metadata) {
      player?.updateLockScreenMetadata(metadata);
    },
  };
}
